import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wrench, ArrowRight, Sun, Moon, Loader2 } from "lucide-react";
import { useApp } from "../context/AppContext";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

export default function Login() {
  const { login, theme, toggleTheme } = useApp();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!username.trim() || !password) {
      setError("Introduce usuario y contraseña");
      return;
    }
    setSubmitting(true);
    try {
      await login(username.trim().toLowerCase(), password);
      toast.success("Acceso correcto");
      navigate("/", { replace: true });
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Credenciales inválidas";
      setError(message);
      toast.error("Acceso denegado", { description: message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="flex-1 flex flex-col bg-white dark:bg-zinc-950 overflow-y-auto no-scrollbar"
      data-testid="login-page"
    >
      <div className="h-3 stripes-warning shrink-0" />

      <div className="flex-1 flex flex-col px-6 pt-6 pb-6">
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-orange-500 flex items-center justify-center rounded-sm shadow-[0_0_20px_rgba(249,115,22,0.5)]">
              <Wrench className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <p className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-orange-500">
                Warranty // Terminal
              </p>
              <h2 className="font-display font-bold text-2xl uppercase tracking-wider text-zinc-900 dark:text-white leading-none mt-1">
                Engine Inspect
              </h2>
            </div>
          </div>
          <button
            type="button"
            onClick={toggleTheme}
            className="w-9 h-9 flex items-center justify-center rounded-sm hover:bg-zinc-100 dark:hover:bg-zinc-900 text-zinc-500 hover:text-orange-500 transition-colors"
            data-testid="login-theme-toggle"
            aria-label="Cambiar tema"
          >
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
        </div>

        <div className="mb-8">
          <p className="font-mono-tech text-[11px] uppercase tracking-widest text-zinc-500 mb-2">
            // Identificación de Operario
          </p>
          <h1 className="font-display font-bold text-4xl sm:text-5xl uppercase leading-[0.95] tracking-tight text-zinc-900 dark:text-white">
            ACCESO<br />
            <span className="text-orange-500">TALLER</span>
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 mt-auto">
          <div>
            <label className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-600 dark:text-zinc-400 block mb-2">
              Usuario
            </label>
            <Input
              data-testid="login-username"
              type="text"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="operario01"
              className="bg-zinc-50 dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 text-zinc-900 dark:text-white font-mono-tech h-14 rounded-sm focus-visible:ring-1 focus-visible:ring-orange-500 focus-visible:border-orange-500 placeholder:text-zinc-400 dark:placeholder:text-zinc-600"
            />
          </div>
          <div>
            <label className="font-mono-tech text-[10px] uppercase tracking-widest text-zinc-600 dark:text-zinc-400 block mb-2">
              Contraseña
            </label>
            <Input
              data-testid="login-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••"
              className="bg-zinc-50 dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 text-zinc-900 dark:text-white font-mono-tech h-14 rounded-sm focus-visible:ring-1 focus-visible:ring-orange-500 focus-visible:border-orange-500 placeholder:text-zinc-400 dark:placeholder:text-zinc-600"
            />
          </div>

          {error && (
            <p className="font-mono-tech text-[11px] uppercase tracking-widest text-red-500 text-center">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="login-submit-btn"
            className="group w-full bg-orange-500 hover:bg-orange-600 disabled:bg-zinc-300 dark:disabled:bg-zinc-800 text-white font-display font-bold uppercase tracking-widest h-14 rounded-sm transition-all glow-orange active:scale-[0.98] flex items-center justify-center gap-2 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                Entrar al taller
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </>
            )}
          </button>
        </form>
      </div>

      <div className="h-3 stripes-warning shrink-0" />
    </div>
  );
}
